// The insight engine (Block E). Every insight is a REAL query against the cube /
// dos-layer — never a narrative without an underlying measure. Each carries the
// spec it was computed from, so "Fäst på dashboard" pins the exact chart behind
// the number. Insights are shown ONLY when they compute and clear a noteworthiness
// threshold; nothing noteworthy → fewer cards, never invented ones.

import { type Period, fetchDosTrend, stgTrendSpec } from './landing'
import { TENANT } from './tenant'
import { formatNumber } from './theme'
import type { Spec } from './types'

export interface Insight {
  id: string
  title: string // rubrik
  value: string // headline värde
  detail: string // what / where / when
  period: string
  source: string // källa
  spec: Spec // the underlying query — pinnable as a card
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function fmtDay(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : s
}

// An all-time record has to carry its year, or the scope of "record" is undiscoverable.
function fmtDayWithYear(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : s
}

// `history` is the whole span the data covers. The day record is measured over it, not
// over the selected period: a record confined to one month is not a record, and it made
// the card change every time a month pill was clicked (Olof: "det kan absolut inte vara
// den dagen").
export async function computeInsights(
  P: Period,
  history?: { from: string; to: string },
): Promise<Insight[]> {
  const record = history ?? { from: P.from, to: P.to }
  const dailySpec = stgTrendSpec({ ...P, from: record.from, to: record.to }, 'day')
  // V1 exposes only the CU day record. Disabled signals must not issue live
  // city/brand queries or delay this already available local-data result.
  const daily = await fetchDosTrend({ manufacturer: TENANT.manufacturer,
    from: record.from, to: record.to, freq: 'day' })

  const out: Insight[] = []

  // 1 — Dagsrekord: the best single day in the whole DOSOR history (the dos-layer).
  const dailyRows = daily?.data ?? []
  const top = dailyRows.reduce<{ name: string; value: number } | null>((best, d) => {
    const v = Number(d.value)
    return !best || v > best.value ? { name: String(d.name), value: v } : best
  }, null)
  if (top && top.value > 0) {
    const allTime = history != null
    out.push({
      id: 'dagsrekord',
      title: allTime ? 'Best day ever' : 'Best single day',
      value: `${formatNumber(top.value)} CU`,
      detail: allTime ? fmtDayWithYear(top.name) : fmtDay(top.name),
      period: allTime ? 'all time' : P.label,
      source: 'from the dose layer',
      spec: dailySpec,
    })
  }

  return out
}
